import React, { SyntheticEvent, useContext, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import styled from '@emotion/styled';
import { Box, Button, Chip, Grid, Stack, Tab, Tabs, TextField, Typography, IconButton } from '@mui/material';
import type { AssetType, InventoryFileEntity } from '@/redux/slices/slice_types';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getTitleInventory, addOrRemoveInventoryUniqueSectionSelection } from '@/redux/slices/orderPlacementSlice';
import { selectFileTypeGroupings } from '@/redux/selectors/constantsSelectors';
import { selectCanDeleteInventoryFile, selectCanReadInventory } from '@/redux/selectors/authSelectors';
import { selectHasInventory, selectInventoryIsLoading } from '@/redux/selectors/orderPlacementSelectors';
import { FileFormat } from '@/constants/constants';
import { useToast } from '@/components/_common/Toast';
import Spinner from '@/components/_common/Spinner';
import NoData from '@/components/Dashboard/NoData';
import PolarisIcon from '@/components/_common/PolarisIcon';
import ErrorFallback from '@/components/_common/ErrorFallback';
import NoAccessSection from '@/components/_common/NoAccessSection';
import NoInventoryV2 from '@/components/TitleDetail/v2/TitleDetailInventoryV2/NoInventoryV2';
import InventoryList from '@/components/TitleDetail/TitleDetailInventory/InventoryList';
import { useTitleURLParser } from '@/components/TitleDetail/v2/hooks/useTitleURLParser';
import { selectActiveTitle } from '@/redux/selectors/titleV2Selectors';
import FileSelect from '../../FileSelect/FileSelect';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { OmakasePlayerClientConfig } from '@/__configs/config_types';
import useFileType from '@/components/_hooks/useFileType';

interface Props {
  selectedVersionName?: string;
  showUnspecified?: boolean;
}

type FileTypes = AssetType | 'all';
type Filters = {
  file_type_filter: FileFormat[];
  search?: string;
  cut_type_filter?: unknown[];
};

export const TITLE_DETAIL_INVENTORY_DROPZONE_ID = 'title-detail-inventory-file-select';

const getNoInventoryType = (entityType: string) => {
  if (entityType === 'episode') {
    return 'episode';
  } else if (entityType === 'season') {
    return 'season';
  } else {
    return 'title';
  }
};

const TitleDetailInventoryV2: React.FC<Props> = ({ selectedVersionName, showUnspecified = false }) => {
  const dispatch = useAppDispatch();
  const { rootTitleId, activeTitleId } = useTitleURLParser();
  const activeTitle = useAppSelector(selectActiveTitle);
  const [loading, setLoading] = useState(false);
  const fileStatusFilter = useContext(ConfigContext)?.titleConfig?.fileStatusFilter;
  const inventoryLoading = useAppSelector(selectInventoryIsLoading) || loading;
  const hasInventory = useAppSelector(selectHasInventory);
  const fileTypeGroupings = useAppSelector(selectFileTypeGroupings);
  const { video, audio, subtitle, image, other } = fileTypeGroupings;
  const canReadInventory = useAppSelector(selectCanReadInventory);
  const canDeleteFile = useAppSelector(selectCanDeleteInventoryFile);
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<Filters>({ file_type_filter: [...video, ...audio, ...subtitle, ...image, ...other] });
  const [activeFileType, setActiveFileType] = useState<FileTypes>('all');
  const omakasePlayerConfig = useConfigValue<OmakasePlayerClientConfig['features']>(['omakasePlayerConfig', 'features']);
  const isOmakasePlayerEnabled = !!omakasePlayerConfig?.length;
  const { error } = useToast();
  const getFileGroup = useFileType();

  useEffect(() => {
    setLoading(true);
    let version_name_filter = null;
    if (!showUnspecified) {
      version_name_filter = selectedVersionName ? [selectedVersionName] : [];
    }
    const promise = dispatch(
      getTitleInventory({
        id: activeTitleId,
        filters: {
          ...filters,
          file_status_filter: fileStatusFilter || ['available', 'archive', 'different', 'future'],
          version_name_filter
        }
      })
    );
    promise.unwrap().finally(() => setLoading(false));
    return () => {
      promise.abort();
      setLoading(false);
    };
  }, [filters, activeTitleId, selectedVersionName, showUnspecified]);

  const onFileTypeSelect = (e: unknown, value: FileTypes) => {
    setActiveFileType(value);
  };

  const submitSearch = (e: SyntheticEvent) => {
    e.preventDefault();
    if (searchInput.length < 2) {
      error('Search term must be longer.');
    } else {
      setFilters({ ...filters, search: searchInput });
    }
  };

  const clearSearch = () => {
    const newFilters = { ...filters };
    delete newFilters.search;
    setFilters(newFilters);
    setSearchInput('');
  };

  const clearFilters = () => {
    setFilters({
      cut_type_filter: [],
      file_type_filter: [...video, ...audio, ...subtitle, ...image, ...other]
    });
    setActiveFileType('all');
    setSearchInput('');
  };

  const hasNoInventory = () => {
    return activeFileType === 'all' && !hasInventory && !filters.search && !filters.file_type_filter?.length && !inventoryLoading;
  };

  const onInventoryFileClick = (file: InventoryFileEntity) => {
    const allowedSections = ['video', 'audio', 'subtitle'];
    if (!isOmakasePlayerEnabled) return;
    const section = getFileGroup(file.file_type);
    if (section && !allowedSections.includes(section)) {
      return error('Only video, audio, and subtitle files can be selected for previewing.');
    }

    dispatch(addOrRemoveInventoryUniqueSectionSelection({ file: { ...file, section }, sections: ['video'] }));
  };

  if (!rootTitleId) return null;
  if (!canReadInventory) return <NoAccessSection />;

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <TitleDetailInventoryV2Wrapper>
        {inventoryLoading && (
          <>
            <StyledLoadingSpinnerBox sx={{ mt: 20 }}>
              <Spinner contained />
              <Typography variant="subtitle2" sx={{ mt: 5, opacity: 0.8 }} align="center">
                Retrieving Inventory
              </Typography>
            </StyledLoadingSpinnerBox>
            <ContentLoadingDiv />
          </>
        )}
        <ContentLoadedDiv loading={inventoryLoading}>
          {!hasNoInventory() && (
            <StyledInventoryFiltersGrid container sx={{ mb: 1 }}>
              <StyledTabs value={activeFileType} onChange={onFileTypeSelect} aria-label="inventory tabs">
                <Tab label="All" value="all" />
                <Tab label="Video" value="video" />
                <Tab label="Audio" value="audio" />
                <Tab label="Subtitle" value="subtitle" />
                <Tab label="Image" value="image" />
                <Tab label="Other" value="other" />
              </StyledTabs>
              {/* SEARCH BAR / SEARCH CHIP */}
              <StyledInventorySearchInputBox>
                <form onSubmit={submitSearch}>
                  {!filters.search ? (
                    <TextField
                      placeholder="Search"
                      type="search"
                      variant="filled"
                      autoComplete="off"
                      size="small"
                      onChange={(e) => setSearchInput(e.target.value)}
                      value={searchInput}
                      InputLabelProps={{ shrink: false }}
                      InputProps={{
                        endAdornment: (
                          <IconButton onClick={submitSearch} size="small">
                            <PolarisIcon icon="search" />
                          </IconButton>
                        )
                      }}
                    />
                  ) : (
                    <StyledSearchChip
                      color="primary"
                      label={
                        <>
                          <strong>Search:</strong> &quot;{filters.search}&quot;
                        </>
                      }
                      onDelete={clearSearch}
                    />
                  )}
                </form>
              </StyledInventorySearchInputBox>
              {isOmakasePlayerEnabled && (
                <Box sx={{ ml: 'auto' }}>
                  <FileSelect rootTitleId={rootTitleId} />
                </Box>
              )}
            </StyledInventoryFiltersGrid>
          )}

          {/* INVENTORY RESULTS */}
          <InventoryList
            filterType={activeFileType}
            disableSelected={!isOmakasePlayerEnabled}
            canDelete={canDeleteFile}
            onClickFile={isOmakasePlayerEnabled ? onInventoryFileClick : undefined}
          />
          {/* NO RESULTS ILLUSTRATION */}
          {!inventoryLoading && !hasInventory && (
            <NoData>
              {activeFileType !== 'all' || filters.search ? (
                <Stack>
                  <div>No inventory found.</div>
                  <Button variant="text" color="primary" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </Stack>
              ) : activeTitle ? (
                <NoInventoryV2 type={getNoInventoryType(activeTitle.entity_type)} />
              ) : null}
            </NoData>
          )}
        </ContentLoadedDiv>
      </TitleDetailInventoryV2Wrapper>
    </ErrorBoundary>
  );
};

export default TitleDetailInventoryV2;

const TitleDetailInventoryV2Wrapper = styled.div`
  position: relative;
  min-height: 400px;
`;

const ContentLoadedDiv = styled('div', {
  shouldForwardProp: (prop) => !['loading'].includes(prop)
})<{ loading: boolean }>`
  ${(props) => (props.loading ? `pointer-events: none;` : null)};
`;

const ContentLoadingDiv = styled.div`
  opacity: 0.8;
  pointer-events: none;
  position: absolute;
  height: 100%;
  width: 100%;
  background: ${(props) => props.theme.palette.background.paper};
  z-index: 9;
`;

const StyledTabs = styled(Tabs)`
  && {
    & .MuiTab-root {
      padding: 0;
      min-width: 80px;
      margin: ${(props) => props.theme.spacing(0, 1, 0, 0)};
      line-height: 1.2;
      min-height: 38px;
      border-radius: ${(props) => props.theme.shape.borderRadius}px;
      background: ${(props) => props.theme.palette.background.default};

      ${(props) => `${props.theme.breakpoints.up('xl')} {
      padding: ${props.theme.spacing(0, 1)};
      min-width: 90px;
    }`};

      & > div {
        pointer-events: none;
      }
    }
    & .MuiTabs-indicator {
      display: none;
    }
    & .Mui-selected {
      background: ${(props) => props.theme.palette.primary.main};
      color: ${(props) => props.theme.palette.getContrastText(props.theme.palette.primary.main)};
    }
  }
`;

const StyledLoadingSpinnerBox = styled(Box)`
  position: absolute;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 99;
  transition: ease top 500ms;
`;

const StyledInventorySearchInputBox = styled(Box)`
  height: 38px;

  @media (min-width: 1290px) {
    padding-left: 20px;
    margin-left: 20px;
    border-left: 1px solid ${(props) => props.theme.palette.primary.dark};
  }

  & input {
    padding-top: 8px;
    padding-bottom: 8px;
  }
`;

const StyledSearchChip = styled(Chip)`
  padding: ${(props) => props.theme.spacing(1)};
  height: 38px;

  & strong {
    margin-right: ${(props) => props.theme.spacing(1)};
  }
`;

const StyledInventoryFiltersGrid = styled(Grid)`
  & .MuiFilledInput-root {
    background-color: ${(props) => props.theme.palette.background.default};
  }
`;
